import { useEffect, useState } from "react";

import { WorkflowRunStatus } from "@/apollo/types";
import dayjs from "@/utils/date";

const POLL_INTERVAL = 60_000;

/** Doubles as a heartbeat, and keeps a monthly schedule under setTimeout's ~24.8 day ceiling. */
const MAX_IDLE_WAIT = 15 * 60_000;

export const isRunInFlight = (status: WorkflowRunStatus | null | undefined) =>
  status === WorkflowRunStatus.NotStarted ||
  status === WorkflowRunStatus.Starting ||
  status === WorkflowRunStatus.Running;

/** When a workflow will next change on its own, or null when nothing is scheduled. */
export const scheduledRunAt = (
  workflow:
    { active: boolean; nextRun?: { dueAt: string } | null } | null | undefined,
) =>
  workflow == null || workflow.active === false || workflow.nextRun == null
    ? null
    : dayjs(workflow.nextRun.dueAt).valueOf();

/**
 * Polls while a run is under way, stops once everything has settled, and wakes up again when the
 * schedule says the next run is due.
 */
export const useOrchestrationPolling = ({
  enabled = true,
  isRunning,
  nextRunAt,
  startPolling,
  stopPolling,
}: {
  enabled?: boolean;
  isRunning: boolean;
  nextRunAt: number | null;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
}) => {
  const [now, setNow] = useState(() => Date.now());

  const isNextRunDue = nextRunAt != null && nextRunAt <= now;
  const shouldPoll = enabled && (isRunning || isNextRunDue);

  // Driven imperatively because clearing `pollInterval` does not stop a poll already running.
  useEffect(() => {
    if (!shouldPoll) {
      return;
    }
    startPolling(POLL_INTERVAL);
    return () => {
      stopPolling();
    };
  }, [shouldPoll, startPolling, stopPolling]);

  // Time is an input as much as the data is: an unchanged response still falls due eventually.
  useEffect(() => {
    if (shouldPoll || nextRunAt == null) {
      return;
    }
    const wakeUp = setTimeout(
      () => {
        setNow(Date.now());
      },
      Math.min(Math.max(nextRunAt - Date.now(), 0), MAX_IDLE_WAIT),
    );
    return () => {
      clearTimeout(wakeUp);
    };
  }, [shouldPoll, nextRunAt, now]);

  return { isNextRunDue };
};
